import { FormEvent, useId, useRef, useState } from 'react';
import { MarkdownText } from '../markdown-text';
import axios from 'axios';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { ServiceDto } from '../../general/dtos';
import { useApi } from '../../providers/api-provider';
import { BookingValues, fieldText, updateBookingValue, validateBooking } from './booking-validation';
import { MessengerPhoneInput } from './messenger-phone-input';
import '../../assets/theme-content.css';

export function ServiceBookingForm({ service, onCancel, onSendingChange }: {
    service: ServiceDto; onCancel: () => void; onSendingChange?: (sending: boolean) => void;
}) {
    const api = useApi();
    const formId = useId();
    const formRef = useRef<HTMLFormElement>(null);
    const inFlight = useRef(false);
    const [values, setValues] = useState<BookingValues>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [attempted, setAttempted] = useState(false);
    const [sending, setSending] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [sent, setSent] = useState(false);
    const definition = service.form_definition;

    const update = (key: string, value: BookingValues[string]) => {
        const next = updateBookingValue(definition, values, key, value);
        setValues(next);
        setSubmitError('');
        if (attempted) setErrors(validateBooking(definition, next).errors);
    };

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (inFlight.current) return;
        const validation = validateBooking(definition, values);
        setAttempted(true);
        setErrors(validation.errors);
        setSubmitError('');
        if (Object.keys(validation.errors).length) {
            const first = Object.keys(validation.errors)[0];
            const control = formRef.current?.querySelector<HTMLElement>(`[data-field-key="${first}"] input, [data-field-key="${first}"] textarea`);
            control?.focus();
            return;
        }
        inFlight.current = true;
        setSending(true);
        onSendingChange?.(true);
        try {
            if (!api) throw new Error('API is initializing');
            await api.submitServiceBooking(service.service_key, { values: validation.answers });
            setSent(true);
        } catch (error) {
            const serverErrors = axios.isAxiosError(error) ? error.response?.data?.errors : undefined;
            if (serverErrors && typeof serverErrors === 'object') {
                const knownErrors: Record<string, string> = {};
                for (const field of definition.fields) {
                    if (typeof serverErrors[field.key] === 'string') knownErrors[field.key] = serverErrors[field.key];
                }
                setErrors(knownErrors);
            }
            setSubmitError('Your request could not be sent. Your answers have been kept; please try again later.');
        } finally {
            inFlight.current = false;
            setSending(false);
            onSendingChange?.(false);
        }
    };

    if (sent) return <div role="status">
        <h3 className="mt-0">Request sent</h3>
        <p>Thank you. I’ll contact you to discuss the details of your request.</p>
        <Button label="Close" onClick={onCancel} />
    </div>;

    return <form ref={formRef} className="service-booking-form theme-content" noValidate onSubmit={submit} aria-label={`Booking request for ${service.name}`}>
        <p className="mt-0">Send your request and I’ll contact you to discuss the details. Fields marked * are required.</p>
        {attempted && Object.keys(errors).length > 0 && <p role="alert" className="text-red-600">Please check the highlighted fields.</p>}
        <div className="grid">
            {definition.fields.map(field => {
                const id = `${formId}-${field.key}`;
                const value = values[field.key];
                const label = fieldText(field.label, field, definition, values);
                const help = field.helpText && fieldText(field.helpText, field, definition, values);
                const error = errors[field.key];
                const describedBy = [help && `${id}-help`, error && `${id}-error`].filter(Boolean).join(' ') || undefined;
                const textValue = typeof value === 'string' ? value : '';
                const className = error ? 'w-full p-invalid' : 'w-full';
                return <div key={field.key} data-field-key={field.key}
                    className={field.type === 'text' || field.type === 'email' ? 'col-12 md:col-6 mb-2' : 'col-12 mb-2'}>
                    {field.type === 'radio' ? <fieldset className="border-none p-0 m-0" aria-describedby={describedBy}>
                        <legend className="font-semibold mb-2 theme-field-label"><MarkdownText text={label} inline />{field.required && ' *'}</legend>
                        <div className="flex flex-wrap gap-4">
                            {field.options?.map(option => <div key={option.value} className="flex align-items-center gap-2">
                                <RadioButton inputId={`${id}-${option.value}`} name={id} value={option.value}
                                    checked={textValue === option.value} disabled={sending} invalid={!!error}
                                    onChange={() => update(field.key, option.value)} />
                                <label htmlFor={`${id}-${option.value}`}><MarkdownText text={option.label} inline /></label>
                            </div>)}
                        </div>
                    </fieldset> : <>
                        <label htmlFor={id} className="block font-semibold mb-2 theme-field-label"><MarkdownText text={label} inline />{field.required && ' *'}</label>
                        {field.type === 'messenger-phone' ? <MessengerPhoneInput id={id}
                            value={typeof value === 'object' ? value : { number: '' }}
                            onChange={phone => update(field.key, phone)} disabled={sending} invalid={!!error} describedBy={describedBy} />
                            : field.type === 'textarea' ? <InputTextarea id={id} value={textValue} rows={4} maxLength={field.maxLength}
                                className={className} disabled={sending} aria-required={field.required} aria-invalid={!!error}
                                aria-describedby={describedBy} onChange={event => update(field.key, event.target.value)} />
                            : <InputText id={id} type={field.type} value={textValue} maxLength={field.maxLength}
                                autoComplete={field.type === 'email' ? 'email' : field.key === 'name' ? 'name' : 'off'}
                                className={className} disabled={sending} aria-required={field.required} aria-invalid={!!error}
                                aria-describedby={describedBy} onChange={event => update(field.key, event.target.value)} />}
                    </>}
                    {help && <MarkdownText id={`${id}-help`} className="text-sm text-color-secondary mt-2" text={help} />}
                    {error && <small id={`${id}-error`} className="block p-error mt-2">{error}</small>}
                </div>;
            })}
        </div>
        {submitError && <p role="alert" className="p-error">{submitError}</p>}
        <div className="flex justify-content-end gap-2 mt-3">
            <Button label="Cancel" type="button" outlined disabled={sending} onClick={onCancel} />
            <Button label="Submit" type="submit" icon="pi pi-send" loading={sending} disabled={sending} />
        </div>
    </form>;
}
